import React, { useState, useEffect, useRef, useCallback } from "react";
import apiClient from "../../api/apiClient";
import { useToast } from "../../context/toastContext";
import { downloadFromApi } from "../../utils/downloadBlob";
import Button from "../ui/Button";
import ShippingLabelDownload from "../ShippingLabelDownload/ShippingLabelDownload";
import "./ShipOrderModal.css";

const CARRIERS = ["UPS", "FedEx", "USPS", "DHL", "Other"];

const FULFILLMENT_METHODS = [
    { key: "seller_delivery", label: "Personal Delivery" },
    { key: "buyer_pickup", label: "Pickup" },
    { key: "carrier_shipping", label: "Professional Delivery" },
];

// The seller must download the Chain of Custody form at least once before
// they can enter shipping info or choose a pickup/delivery method — this
// modal gates the fulfillment form behind that download. Once downloaded
// (tracked server-side via coc_pdf_downloaded_at), re-downloading it on a
// later visit to this modal is optional, not required — the fulfillment
// form still offers a button to fetch it again (e.g. a lost printout).
const ShipOrderModal = ({ order, onShip, onCancel }) => {
    const cancelRef = useRef(null);
    const toast = useToast();

    const [fulfillmentMethod, setFulfillmentMethod] = useState("carrier_shipping");
    const [trackingNumber, setTrackingNumber] = useState("");
    const [carrier, setCarrier] = useState("");
    const [customCarrier, setCustomCarrier] = useState("");
    const [isShipping, setIsShipping] = useState(false);

    const [isPreparing, setIsPreparing] = useState(true);
    const [cocReady, setCocReady] = useState(false);
    const [hasDownloaded, setHasDownloaded] = useState(!!order?.coc_pdf_downloaded_at);
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onCancel();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onCancel]);

    // Ensure the Chain of Custody PDF exists before letting the seller proceed.
    // The order prop already tells us whether it's generated (set as soon as
    // payment was confirmed); this only hits the network to self-heal orders
    // that predate that trigger, or where generation failed silently.
    const ensureCocReady = useCallback(async () => {
        if (!order?.id) return;
        if (order.coc_pdf_generated_at) {
            setCocReady(true);
            setIsPreparing(false);
            return;
        }
        setIsPreparing(true);
        try {
            await apiClient.post(`/orders/${order.id}/coc-pdf/regenerate`);
            setCocReady(true);
        } catch {
            setCocReady(false);
        } finally {
            setIsPreparing(false);
        }
    }, [order?.id, order?.coc_pdf_generated_at]);

    useEffect(() => {
        ensureCocReady();
    }, [ensureCocReady]);

    const pickupEnabled = order?.allow_buyer_pickup === 1 || order?.allow_buyer_pickup === true;
    const personalDeliveryEnabled = order?.allow_self_delivery === 1 || order?.allow_self_delivery === true;
    const methodEnabled = fulfillmentMethod === "carrier_shipping" ||
        (fulfillmentMethod === "buyer_pickup" && pickupEnabled) ||
        (fulfillmentMethod === "seller_delivery" && personalDeliveryEnabled);
    const canShip = methodEnabled && (
        fulfillmentMethod !== "carrier_shipping" || trackingNumber.trim().length > 0
    );

    const address = fulfillmentMethod === "buyer_pickup"
        ? [order?.pickup_address, order?.pickup_city, order?.pickup_state, order?.pickup_zip_code]
        : [
            order?.buyer_location_address || order?.buyer_address,
            order?.buyer_location_city || order?.buyer_city,
            order?.buyer_location_state || order?.buyer_state,
            order?.buyer_location_zip_code || order?.buyer_zip_code,
        ];
    const formattedAddress = address.filter(Boolean).join(", ");

    const handleDownloadCoc = async () => {
        if (!order?.id || isDownloading) return;
        setIsDownloading(true);
        try {
            await downloadFromApi(`/orders/${order.id}/coc-pdf`, `chain-of-custody-order-${order.id}.pdf`);
            setHasDownloaded(true);
        } catch {
            toast.error("Failed to download the Chain of Custody form. Please try again.");
        } finally {
            setIsDownloading(false);
        }
    };

    const handleShip = async () => {
        if (!canShip || isShipping) return;
        setIsShipping(true);

        const shippingCarrier = carrier === "Other"
            ? customCarrier.trim() || "Other"
            : carrier || null;

        try {
            await onShip({
                fulfillment_method: fulfillmentMethod,
                ...(fulfillmentMethod === "carrier_shipping" && {
                    tracking_number: trackingNumber.trim(),
                    shipping_carrier: shippingCarrier,
                }),
            });
        } finally {
            setIsShipping(false);
        }
    };

    const showFulfillmentForm = !isPreparing && cocReady && hasDownloaded;

    return (
        <div className="tw-modal-overlay" onClick={onCancel}>
            <div
                className="tw-modal ship-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="ship-modal-title"
            >
                <div className="tw-modal__header">
                    <h3 className="tw-modal__title" id="ship-modal-title">
                        {showFulfillmentForm ? "Fulfill Order" : "Chain of Custody Form"}
                    </h3>
                    <button className="tw-modal__close" onClick={onCancel} aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="tw-modal__body ship-modal__body">
                    {isPreparing && (
                        <p className="ship-modal__status-text">
                            Preparing the Chain of Custody form…
                        </p>
                    )}

                    {!isPreparing && !cocReady && (
                        <p className="ship-modal__status-text ship-modal__status-text--error">
                            We couldn't prepare the Chain of Custody form for this order.
                            Please try again.
                        </p>
                    )}

                    {!isPreparing && cocReady && !hasDownloaded && (
                        <p className="ship-modal__gate-text">
                            Before this order can be fulfilled, download and print the Chain
                            of Custody form. Both parties sign it, and the buyer uploads a
                            photo of the signed copy — along with packaging photos — to
                            complete the order.
                        </p>
                    )}

                    {showFulfillmentForm && (
                        <>
                            {order && (
                                <p className="ship-modal__order-info">
                                    {order.product_name} for{" "}
                                    <strong>{order.requester_business_name || order.requester_license_no}</strong>
                                </p>
                            )}

                            <div className="ship-modal__tabs">
                                {FULFILLMENT_METHODS.map((method) => {
                                    const enabled = method.key === "carrier_shipping" ||
                                        (method.key === "buyer_pickup" && pickupEnabled) ||
                                        (method.key === "seller_delivery" && personalDeliveryEnabled);
                                    return (
                                        <button
                                            key={method.key}
                                            type="button"
                                            role="tab"
                                            className={`ship-modal__tab${fulfillmentMethod === method.key ? " ship-modal__tab--active" : ""}`}
                                            onClick={() => enabled && setFulfillmentMethod(method.key)}
                                            disabled={!enabled}
                                            title={enabled ? undefined : "Enable this option in Company Settings"}
                                        >
                                            {method.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {fulfillmentMethod === "seller_delivery" && (
                                <ShippingLabelDownload order={order} />
                            )}

                            {fulfillmentMethod === "carrier_shipping" ? (
                                <>
                                    <div className="ship-modal__field">
                                        <label className="ship-modal__label" htmlFor="ship-tracking">
                                            Tracking Number *
                                        </label>
                                        <input
                                            id="ship-tracking"
                                            className="ship-modal__input"
                                            type="text"
                                            value={trackingNumber}
                                            onChange={(e) => setTrackingNumber(e.target.value)}
                                            placeholder="Enter tracking number"
                                            autoFocus
                                        />
                                    </div>

                                    <div className="ship-modal__field">
                                        <label className="ship-modal__label" htmlFor="ship-carrier">
                                            Shipping Carrier
                                        </label>
                                        <select
                                            id="ship-carrier"
                                            className="ship-modal__select"
                                            value={carrier}
                                            onChange={(e) => setCarrier(e.target.value)}
                                        >
                                            <option value="">Select carrier (optional)</option>
                                            {CARRIERS.map((c) => (
                                                <option key={c} value={c}>{c}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {carrier === "Other" && (
                                        <div className="ship-modal__field">
                                            <label className="ship-modal__label" htmlFor="ship-custom-carrier">
                                                Carrier Name
                                            </label>
                                            <input
                                                id="ship-custom-carrier"
                                                className="ship-modal__input"
                                                type="text"
                                                value={customCarrier}
                                                onChange={(e) => setCustomCarrier(e.target.value)}
                                                placeholder="Enter carrier name"
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="ship-modal__method-panel">
                                    <strong>
                                        {fulfillmentMethod === "buyer_pickup" ? "Pickup address" : "Delivery address"}
                                    </strong>
                                    <p>{formattedAddress || "No complete address is available."}</p>
                                    <small>
                                        {fulfillmentMethod === "buyer_pickup"
                                            ? "The buyer will collect this order, then confirm receipt by uploading a packaging photo and a photo of the signed Chain of Custody form."
                                            : "Your company will deliver this order, then the buyer will confirm receipt by uploading a packaging photo and a photo of the signed Chain of Custody form."}
                                    </small>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="ship-modal__footer">
                    {showFulfillmentForm && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="ship-modal__redownload-btn"
                            onClick={handleDownloadCoc}
                            disabled={isDownloading}
                            loading={isDownloading}
                        >
                            Download Chain of Custody Form
                        </Button>
                    )}

                    <Button
                        ref={cancelRef}
                        variant="secondary"
                        size="sm"
                        onClick={onCancel}
                        disabled={isShipping}
                    >
                        Cancel
                    </Button>

                    {!isPreparing && !cocReady && (
                        <Button variant="primary" size="sm" onClick={ensureCocReady}>
                            Retry
                        </Button>
                    )}

                    {!isPreparing && cocReady && !hasDownloaded && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleDownloadCoc}
                            disabled={isDownloading}
                            loading={isDownloading}
                        >
                            Download Chain of Custody Form
                        </Button>
                    )}

                    {showFulfillmentForm && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleShip}
                            disabled={!canShip || isShipping}
                            loading={isShipping}
                        >
                            {fulfillmentMethod === "carrier_shipping" ? "Ship Order" : "Confirm Method"}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShipOrderModal;
